/**
 * How complete a school's self-service app setup is (release lines U10, R17,
 * R18).
 *
 * A school with no live app fills a guided setup before School Journey
 * provisions anything. Staff see, per setup, which of the setup's fields the
 * school has filled. The fields are R17's list, each read from where the school
 * editor already writes it (U8), never from a setup-only copy:
 *
 *  - `app-name`, `app-icon`: the U6 draft store (or the registry row, when staff
 *    already set it).
 *  - `store-title`, `store-description`: the working store listing, filled on
 *    at least one store.
 *  - `welcome-text`, `login-text`: the pre-login copy, filled once it differs
 *    from the default copy every new app is seeded with. The seed is placeholder
 *    text, not the school's.
 *  - `welcome-images`: a photo on every onboarding slide that has a photo slot.
 *  - `login-bg`: the login background photo.
 *  - `logo-light`, `logo-dark`: the school's logos (the design page's General
 *    tab). The app's own logo slots are staff-only (KTD13), so the school's
 *    logos are what staff start from.
 *
 * Computed on read, never stored, like the rest of readiness.
 */

import { z } from 'zod'

export const SCHOOL_APP_SETUP_FIELDS = [
  'app-name',
  'app-icon',
  'store-title',
  'store-description',
  'welcome-text',
  'welcome-images',
  'login-text',
  'login-bg',
  'logo-light',
  'logo-dark'
] as const
export const SchoolAppSetupFieldSchema = z.enum(SCHOOL_APP_SETUP_FIELDS)
export type SchoolAppSetupField = z.infer<typeof SchoolAppSetupFieldSchema>

/** Each setup field in the words staff and the school see. */
export const SCHOOL_APP_SETUP_FIELD_LABELS: Record<SchoolAppSetupField, string> = {
  'app-name': 'App name',
  'app-icon': 'App icon',
  'store-title': 'Store title',
  'store-description': 'Store description',
  'welcome-text': 'Welcome text',
  'welcome-images': 'Welcome screen photos',
  'login-text': 'Login text',
  'login-bg': 'Login background',
  'logo-light': 'Logo (light)',
  'logo-dark': 'Logo (dark)'
}

export const SchoolAppSetupCompletenessSchema = z.object({
  /** Every setup field, in `SCHOOL_APP_SETUP_FIELDS` order. */
  fields: z.array(z.object({ field: SchoolAppSetupFieldSchema, filled: z.boolean() })),
  filledCount: z.number().int().nonnegative(),
  totalCount: z.number().int().positive(),
  /** The unfilled fields, in the same order. Empty when the setup is complete. */
  missing: z.array(SchoolAppSetupFieldSchema)
})
export type SchoolAppSetupCompleteness = z.infer<typeof SchoolAppSetupCompletenessSchema>
